import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ApiError } from '../../api/client';
import { useSubjectFieldsMutation } from '../../planner/api';
import type { FieldInput, FieldKind, Subject } from '../../planner/api';

type Row = { key: number; id?: string; label: string; kind: FieldKind; optionsText: string; min: string; max: string };

const KINDS: { value: FieldKind; label: string }[] = [
  { value: 'choice', label: 'Keuze uit een lijst' },
  { value: 'number', label: 'Getal' },
  { value: 'text', label: 'Tekst' },
];

const MAX_FIELDS = 8;

function errorText(error: unknown): string {
  if (error instanceof ApiError && error.status === 400) {
    return 'Controleer de velden: elke naam is uniek (max. 30 tekens), een keuzelijst heeft minstens één keuze en bij een getal is het kleinste getal niet groter dan het grootste.';
  }
  return 'Opslaan is niet gelukt. Probeer het opnieuw.';
}

/** Where the teacher decides which fields a subject has: Rekenen "Blok" and "Les", Gym nothing at all. */
export default function SubjectFieldsEditor({ subject, onClose }: { subject: Subject; onClose: () => void }) {
  const save = useSubjectFieldsMutation();
  const [nextKey, setNextKey] = useState(subject.fields.length);
  const [rows, setRows] = useState<Row[]>(() =>
    subject.fields.map((field, index) => ({
      key: index,
      id: field.id,
      label: field.label,
      kind: field.kind,
      optionsText: field.options.join('\n'),
      min: String(field.min),
      max: String(field.max),
    })),
  );

  const change = (key: number, patch: Partial<Row>) => setRows(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  const move = (index: number, delta: number) => {
    const next = [...rows];
    const [item] = next.splice(index, 1);
    if (!item) return;
    next.splice(index + delta, 0, item);
    setRows(next);
  };
  const add = () => {
    setRows([...rows, { key: nextKey, label: '', kind: 'choice', optionsText: '', min: '1', max: '20' }]);
    setNextKey(nextKey + 1);
  };

  const submit = () => {
    const fields: FieldInput[] = rows.map((row) => ({
      ...(row.id ? { id: row.id } : {}),
      label: row.label.trim(),
      kind: row.kind,
      ...(row.kind === 'choice' ? { options: row.optionsText.split('\n').map((option) => option.trim()).filter(Boolean) } : {}),
      ...(row.kind === 'number' ? { min: Number(row.min), max: Number(row.max) } : {}),
    }));
    save.mutate({ id: subject.id, fields }, { onSuccess: onClose });
  };

  return (
    <div className="flex flex-col gap-3 border p-3">
      <p className="text-sm opacity-80">
        Velden die je bij een les van {subject.name} kunt invullen, bijvoorbeeld een Blok (keuze) en een Les (getal). Een vak zonder velden werkt gewoon met alleen een notitie.
      </p>
      {rows.map((row, index) => (
        <fieldset key={row.key} className="flex flex-wrap items-start gap-3 border-t pt-3">
          <legend className="sr-only">Veld {index + 1}</legend>
          <label className="flex flex-col gap-1">
            Naam
            <input value={row.label} maxLength={30} onChange={(event) => change(row.key, { label: event.target.value })} className="w-40" />
          </label>
          <label className="flex flex-col gap-1">
            Soort
            <select value={row.kind} onChange={(event) => change(row.key, { kind: event.target.value as FieldKind })} className="min-h-11 rounded border px-2">
              {KINDS.map((kind) => (
                <option key={kind.value} value={kind.value}>
                  {kind.label}
                </option>
              ))}
            </select>
          </label>
          {row.kind === 'choice' && (
            <label className="flex min-w-48 flex-1 flex-col gap-1">
              Keuzes, één per regel
              <textarea
                value={row.optionsText}
                rows={Math.min(8, Math.max(3, row.optionsText.split('\n').length))}
                placeholder={'Blok 1\nBlok 2\nBlok 3'}
                onChange={(event) => change(row.key, { optionsText: event.target.value })}
              />
            </label>
          )}
          {row.kind === 'number' && (
            <>
              <label className="flex flex-col gap-1">
                Van
                <input type="number" value={row.min} min={0} max={9999} onChange={(event) => change(row.key, { min: event.target.value })} className="w-24" />
              </label>
              <label className="flex flex-col gap-1">
                Tot en met
                <input type="number" value={row.max} min={0} max={9999} onChange={(event) => change(row.key, { max: event.target.value })} className="w-24" />
              </label>
            </>
          )}
          <span className="flex-1" />
          <Button type="button" variant="link" className="h-auto min-h-11 px-2" aria-label={`Veld ${row.label || index + 1} omhoog`} disabled={index === 0} onClick={() => move(index, -1)}>
            ↑
          </Button>
          <Button type="button" variant="link" className="h-auto min-h-11 px-2" aria-label={`Veld ${row.label || index + 1} omlaag`} disabled={index === rows.length - 1} onClick={() => move(index, 1)}>
            ↓
          </Button>
          <Button type="button" variant="link" className="h-auto min-h-11 px-0" aria-label={`Verwijder veld ${row.label || index + 1}`} onClick={() => setRows(rows.filter((r) => r.key !== row.key))}>
            Verwijderen
          </Button>
        </fieldset>
      ))}
      {rows.length === 0 && <p className="text-sm">Dit vak heeft nog geen velden.</p>}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="outline" className="min-h-11" disabled={rows.length >= MAX_FIELDS} onClick={add}>
          Veld toevoegen
        </Button>
        <span className="flex-1" />
        <Button type="button" className="min-h-11" disabled={save.isPending} onClick={submit}>
          Opslaan
        </Button>
        <Button type="button" variant="link" className="h-auto min-h-11 px-0" onClick={onClose}>
          Annuleren
        </Button>
      </div>
      {save.isError && (
        <p role="alert" className="text-sm">
          {errorText(save.error)}
        </p>
      )}
    </div>
  );
}
